import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { usePotwierdzenie } from '@/components/OknoPotwierdzenia';
import { Check, ChevronRight, Eraser, History, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/toast';
import { useI18n } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { fetchWdrozenie, type StanWdrozenia } from '@/lib/wdrozenie-api';
import { porzucZaleglosc, przewinWydania, zdejmijGraniceWydan } from '@/lib/wyspa-api';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ZALEGŁE WYDANIA Z CZASU INSTALACJI — „Roześlij zaległe wydania od".
 *
 *  ⚠️⚠️ ZGŁOSZONE (01.09.2026, IKEA — 12 automatów przy pracującej fabryce): „przycisk, który
 *  zaciągnie od jakiegoś momentu transaction loga ze wszystkich maszyn i wyrówna mastera (…) nie
 *  wiem, czy poprawnie poszły na mastera". NIE POSZŁY: włączenie rozsyłania ustawia kursory na
 *  BIEŻĄCY SZCZYT, więc pobrania sprzed włączenia leżą pod kursorem. Reguła jest słuszna (inaczej
 *  pierwsze włączenie wsypywałoby całą historię), brakowało drogi powrotnej.
 *
 *  ⚠️⚠️ JEDEN PRZEBIEG — zamówione 29.09.2026: „powinien zrobić jeden przebieg i po zakończeniu nie
 *  wyświetlać info o granicy (…) jak raz roześlemy, opcja powinna po wdrożeniu dawać już tylko info.
 *  Samą funkcjonalność możemy przenieść do drugiej zakładki". Stąd trzy kawałki: formularz (kreator
 *  daje go tylko przed pierwszym przebiegiem), belka ze śladem przebiegu i pełny panel
 *  w „Synchronizacjach". Granicę czasu instancja zdejmuje sama, gdy ostatni węzeł dociągnie historię.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */

type Nadrobienie = NonNullable<StanWdrozenia['wyspa']['nadrobienie']>;
type WynikPrzewiniecia = Awaited<ReturnType<typeof przewinWydania>>;

/** Wygląd belki per stan — semantyka, nie akcent: trwa / dojechało / świadomie przerwane. */
const WYGLAD: Record<Nadrobienie['stan'], { ikona: typeof Check; klasa: string; ikonaKlasa: string }> = {
  W_TOKU: { ikona: Loader2, klasa: 'border-primary/30 bg-primary/5', ikonaKlasa: 'animate-spin text-primary' },
  ZAKONCZONE: { ikona: Check, klasa: 'border-[var(--success)]/40 bg-[var(--success)]/5', ikonaKlasa: 'text-[var(--success)]' },
  PRZERWANE: { ikona: Eraser, klasa: 'border-[var(--warning)]/40 bg-muted/40', ikonaKlasa: 'text-[var(--warning)]' },
};

/**
 * BELKA — ślad ostatniego przebiegu, bez żadnego przycisku („napisać, że była taka synchronizacja
 * zapuszczana i tyle").
 *
 * ⚠️ STAN LICZY INSTANCJA Z KURSORÓW WĘZŁÓW (`stan-nadrobienia.ts`), belka go tylko rysuje — ta sama
 * zasada, co cały kreator: pokazuje stan, a nie liczy kliknięć.
 */
export function PasekNadrobienia({ nadrobienie }: { nadrobienie: Nadrobienie }) {
  const { t } = useI18n();
  const { ikona: Ikona, klasa, ikonaKlasa } = WYGLAD[nadrobienie.stan];
  return (
    <p className={cn('flex w-fit flex-wrap items-center gap-x-1.5 gap-y-0.5 rounded-md border px-2 py-1 text-[11px]', klasa)}>
      <Ikona className={cn('size-3.5 shrink-0', ikonaKlasa)} aria-hidden />
      <span>{t(`ss.zalegle.${nadrobienie.stan}`).replace('{od}', new Date(nadrobienie.od).toLocaleString('pl'))}</span>
      {/* ⚠️ Przebieg sprzed 29.09.2026 bez śladu w audycie nie ma chwili zlecenia — wtedy jej nie zmyślamy. */}
      {nadrobienie.zlecono !== null && (
        <span className="text-muted-foreground">
          · {t('ss.zalegle.zlecone').replace('{data}', new Date(nadrobienie.zlecono).toLocaleString('pl'))}
        </span>
      )}
    </p>
  );
}

/**
 * FORMULARZ „ROZEŚLIJ ZALEGŁE WYDANIA OD".
 *
 * ⚠️ W KREATORZE STOI TYLKO DO PIERWSZEGO PRZEBIEGU — potem kreator pokazuje belkę, a formularz
 * żyje w „Synchronizacjach", gdyby naprawdę trzeba było coś dosłać.
 */
export function FormularzNadrobienia({ groupId, onWynik }: { groupId: string; onWynik?: (w: WynikPrzewiniecia) => void }) {
  const { t } = useI18n();
  const toast = useToast();
  const qc = useQueryClient();
  /** Chwila, od której rozesłać zaległe wydania — pole `datetime-local`, więc czas LOKALNY. */
  const [odKiedy, setOdKiedy] = useState('');

  const przewin = useMutation({
    /**
     * ⚠️ POLE `datetime-local` ODDAJE CZAS LOKALNY BEZ STREFY, a API oczekuje ISO ze strefą —
     * `new Date(...)` w przeglądarce czyta to jako czas lokalny i `toISOString()` dokłada strefę.
     * Bez tej zamiany „od 6:00" znaczyłoby dla portalu 6:00 UTC, czyli 8:00 u klienta w lipcu.
     */
    mutationFn: (lokalna: string) => przewinWydania(groupId, new Date(lokalna).toISOString()),
    onSuccess: async (w) => {
      onWynik?.(w);
      toast.success(
        w.wydanOd === null
          ? t('ss.k7.przewinNic')
          : t('ss.k7.przewinWynik').replace('{wezly}', String(w.wezly.length)).replace('{n}', String(w.doRozeslania)),
      );
      /** ⚠️ Ślad przebiegu przychodzi ze stanem kreatora — bez odświeżenia kreator dalej dawałby formularz. */
      await qc.invalidateQueries({ queryKey: ['wdrozenie', groupId] });
      await qc.invalidateQueries({ queryKey: ['agents'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="flex flex-wrap items-end gap-2">
      <label className="flex flex-col gap-1 text-[11px]">
        <span>{t('ss.k7.przewinOd')}</span>
        <Input type="datetime-local" className="h-8 w-56" value={odKiedy} onChange={(e) => setOdKiedy(e.target.value)} />
      </label>
      <Button size="sm" variant="outline" disabled={odKiedy === '' || przewin.isPending} onClick={() => przewin.mutate(odKiedy)}>
        {przewin.isPending ? <Loader2 className="size-4 animate-spin" /> : <History className="size-4" />}
        {t('ss.k7.przewinButton')}
      </Button>
      <p className="w-full text-[11px] text-muted-foreground">{t('ss.k7.przewinPoCo')}</p>
    </div>
  );
}

/** CO POSZŁO DO KTÓREGO WĘZŁA — wynik jednego kliknięcia „Roześlij", z rozbiciem na automaty. */
function WynikRozeslania({ wynik }: { wynik: WynikPrzewiniecia }) {
  const { t } = useI18n();
  /** Które węzły mają rozwinięte rozbicie liczby wydań — trzymamy rozwinięte, nie zwinięte. */
  const [rozwinieteZrodla, setRozwinieteZrodla] = useState<ReadonlySet<string>>(new Set());

  if (wynik.wezly.length === 0) return <p className="text-[11px] text-muted-foreground">{t('ss.k7.przewinNic')}</p>;
  return (
    <ul className="w-full space-y-0.5 text-[11px]">
      {wynik.wezly.map((w) => {
        const zrodla = w.zrodla ?? [];
        const rozwiniety = rozwinieteZrodla.has(w.nodeId);
        return (
          <li key={w.nodeId}>
            <span className="font-mono">{w.nodeId}</span>{' '}
            {/*
              ⚠️⚠️ LICZBA JEST KLIKALNA (zamówione 02.09.2026): „kliknięcie na ilość wydań, żeby
              pokazało skąd ta liczba się wzięła — bo to raczej jest rozbite, że z każdej maszyny idzie
              jakaś część". Tak właśnie jest: każdy węzeł dostaje sumę wydań ze WSZYSTKICH POZOSTAŁYCH
              maszyn, więc bez rozbicia siedem różnych liczb nie da się z niczym porównać ani sprawdzić.

              ⚠️ ROZBICIE JEDZIE Z SERWERA, a suma pozycji JEST tą liczbą — nie składamy jej tutaj drugi raz.
            */}
            {zrodla.length === 0 ? (
              <span className="text-muted-foreground">{t('ss.k7.przewinWezel').replace('{n}', String(w.doRozeslania))}</span>
            ) : (
              <button
                type="button"
                className="underline decoration-dotted text-muted-foreground hover:text-foreground"
                aria-expanded={rozwiniety}
                title={t('ss.k7.skadDymek')}
                onClick={() =>
                  setRozwinieteZrodla((byly) => {
                    const teraz = new Set(byly);
                    if (!teraz.delete(w.nodeId)) teraz.add(w.nodeId);
                    return teraz;
                  })
                }
              >
                {t('ss.k7.przewinWezel').replace('{n}', String(w.doRozeslania))}
                <ChevronRight className={cn('inline size-3 transition-transform', rozwiniety && 'rotate-90')} aria-hidden />
              </button>
            )}
            {rozwiniety && (
              <ul className="mb-1 ml-4 mt-0.5 space-y-0.5 border-l border-border pl-2">
                {zrodla.map((z) => (
                  <li key={z.vmid} className="text-muted-foreground">
                    {t('ss.k7.skadPozycja')
                      .replace('{automat}', z.nazwa)
                      .replace('{vmid}', String(z.vmid))
                      .replace('{n}', String(z.wydan))}
                  </li>
                ))}
                {/* ⚠️ WŁASNE WYDANIA WĘZŁA TU NIE STOJĄ i trzeba to napisać — inaczej brak jednej
                    maszyny na liście czyta się jak zgubiona część sumy. */}
                <li className="text-[10px] text-muted-foreground/80">{t('ss.k7.skadStopka')}</li>
              </ul>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * PANEL W „SYNCHRONIZACJACH" — ponowne dosłanie, przerwanie i granica trwającego przebiegu.
 *
 * ⚠️ TEN SAM KLUCZ ZAPYTANIA CO KREATOR, więc panel nie kosztuje dodatkowego wywołania.
 * ⚠️ BEZ WŁĄCZONEGO ROZSYŁANIA PANELU NIE MA — nie ma czym dosyłać; włącza się je w kroku 7 kreatora.
 */
export function ZalegleWydania({ groupId }: { groupId: string }) {
  const { t } = useI18n();
  const { zapytaj, oknoPytania } = usePotwierdzenie();
  const toast = useToast();
  const qc = useQueryClient();
  const wdrozenie = useQuery({ queryKey: ['wdrozenie', groupId], queryFn: () => fetchWdrozenie(groupId) });
  const [wynik, setWynik] = useState<WynikPrzewiniecia | null>(null);

  /**
   * ═════════════════════════════════════════════════════════════════════
   *  HAMULEC DOCIĄGANIA HISTORII — zgłoszone z Magny 09.09.2026: „trwa to już dobę,
   *  a nie jesteśmy nawet w połowie".
   *
   *  ⚠️⚠️ DOTĄD PORTAL NIE MIAŁ CZYM TEGO ZATRZYMAĆ. „Przewiń do teraz" jest świadomie
   *  odmawiane węzłowi z kursorem niższym od celu (byłoby cichym pominięciem wierszy), a wyłącznik
   *  rozsyłania zatrzymuje też wydania BIEŻĄCE — czyli zabiera limitom wiedzę o dzisiejszych
   *  pobraniach. Stąd osobna, nazwana czynność: pominięcie historii jako DECYZJA.
   * ═════════════════════════════════════════════════════════════════════
   */
  const porzuc = useMutation({
    mutationFn: () => porzucZaleglosc(groupId),
    onSuccess: async (w) => {
      toast.success(t('ss.k7.porzuconoWynik').replace('{n}', w.porzuconychRazem.toLocaleString('pl')));
      await qc.invalidateQueries({ queryKey: ['wdrozenie'] });
      await qc.invalidateQueries({ queryKey: ['agents'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  /**
   * ⚠️⚠️ ZDJĘCIE GRANICY CZASU W TRAKCIE PRZEBIEGU. Po przebiegu instancja zdejmuje ją sama;
   * w trakcie zdjęcie znaczy „zaległość pojedzie w całości, bez daty" — rzadka, świadoma decyzja,
   * więc przycisk stoi tu, a nie w kreatorze.
   */
  const bezGranicy = useMutation({
    mutationFn: () => zdejmijGraniceWydan(groupId),
    onSuccess: async (w) => {
      toast.success(w.zdjeto ? t('ss.k7.granicaZdjeta') : t('ss.k7.granicaBrak'));
      await qc.invalidateQueries({ queryKey: ['wdrozenie'] });
      await qc.invalidateQueries({ queryKey: ['islands'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const wyspa = wdrozenie.data?.wyspa;
  if (wyspa === undefined || !wyspa.wydaniaRodzenstwa) return null;

  return (
    <section className="space-y-2 rounded-lg border border-border p-3">
      {oknoPytania}
      <h2 className="text-sm font-medium">{t('ss.zalegle.tytul')}</h2>
      {wyspa.nadrobienie != null && <PasekNadrobienia nadrobienie={wyspa.nadrobienie} />}
      {/*
        ⚠️⚠️ GRANICA CZASU WIDOCZNA, BO TNIE ZALEGŁOŚĆ. Do 14.09.2026 tej wartości nie było widać
        NIGDZIE, a IKEA Babimost stała z kursorami bez ruchu przez trzy doby. Od 29.09.2026 granica
        żyje tylko przez jeden przebieg — schodzi sama, gdy ostatni węzeł dociągnie historię — więc
        ta ramka pokazuje się wyłącznie, póki przebieg trwa.
      */}
      {wyspa.wydaniaOdKiedy != null && (
        <div className="flex w-full flex-wrap items-center gap-2 rounded-md border border-[var(--warning)]/40 bg-muted/40 p-2 text-[11px]">
          <span className="text-[var(--warning)]">
            {t('ss.k7.granicaJest').replace('{data}', new Date(wyspa.wydaniaOdKiedy).toLocaleString('pl'))}
          </span>
          <Button size="sm" variant="outline" className="h-6" disabled={bezGranicy.isPending} onClick={() => bezGranicy.mutate()}>
            {bezGranicy.isPending && <Loader2 className="size-3 animate-spin" />}
            {t('ss.k7.granicaZdejmij')}
          </Button>
        </div>
      )}
      <FormularzNadrobienia groupId={groupId} onWynik={setWynik} />
      {/*
        ⚠️⚠️ PYTANIE PRZED PORZUCENIEM, bo tego się nie cofa: wiersze pominięte zostają pominięte.
        Zdanie w pytaniu mówi, co dokładnie przestanie jechać — i że wydania BIEŻĄCE jadą dalej,
        bo to jest jedyny powód, dla którego wolno to kliknąć spokojnie.
      */}
      <Button
        size="sm"
        variant="outline"
        disabled={porzuc.isPending}
        onClick={async () => {
          const tytul = t('ss.k7.porzucButton');
          if (!(await zapytaj({ tytul, tresc: t('ss.k7.porzucPytanie'), etykietaPotwierdz: tytul, ton: 'grozne' }))) return;
          porzuc.mutate();
        }}
        title={t('ss.k7.porzucPoCo')}
      >
        {porzuc.isPending ? <Loader2 className="size-4 animate-spin" /> : <Eraser className="size-4" />}
        {t('ss.k7.porzucButton')}
      </Button>
      {wynik !== null && <WynikRozeslania wynik={wynik} />}
    </section>
  );
}
